import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QrCode, Printer, Download } from 'lucide-react';
import { Product } from '@/context/SupabaseInventoryContext';
interface QRCodeModalProps {
  product: Product;
  children?: React.ReactNode;
}
export const QRCodeModal: React.FC<QRCodeModalProps> = ({
  product,
  children
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [open, setOpen] = useState(false);
  const generateQRCode = async () => {
    setIsGenerating(true);
    try {
      // Utiliser l'ID du produit comme données du QR Code
      const qrData = product.barcode || product.id;
      const url = await QRCode.toDataURL(qrData, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(url);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  };
  useEffect(() => {
    generateQRCode();
  }, [product]);
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${product.name}</title>
          <style>
            @media print {
              body { margin: 0; }
              .print-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 20px;
                font-family: Arial, sans-serif;
              }
              .qr-code {
                margin: 20px 0;
              }
              .product-info {
                text-align: center;
                margin-top: 10px;
              }
              .product-name {
                font-size: 18px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              .product-barcode {
                font-size: 12px;
                color: #666;
                font-family: monospace;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="product-info">
              <div class="product-name">${product.name}</div>
              ${product.barcode ? `<div class="product-barcode">Code-barres: ${product.barcode}</div>` : ''}
            </div>
            <div class="qr-code">
              <img src="${qrCodeUrl}" alt="QR Code" />
            </div>
            <div class="product-info">
              <div style="font-size: 10px; color: #999;">ID: ${product.id}</div>
            </div>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  const handleDownload = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `qr-code-${product.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || <Button variant="outline" size="sm" className="flex items-center gap-2">
            <QrCode className="w-4 h-4" />
            
          </Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" onInteractOutside={() => setOpen(false)} onPointerDownOutside={() => setOpen(false)} onEscapeKeyDown={() => setOpen(false)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left px-0 mx-[60px]">
            <QrCode className="w-5 h-5" />
            QR Code - {product.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-4">
          {isGenerating ? <div className="w-64 h-64 bg-muted rounded-lg flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div> : qrCodeUrl && <div className="bg-white p-4 rounded-lg border">
                <img src={qrCodeUrl} alt={`QR Code pour ${product.name}`} className="w-64 h-64" />
              </div>}
          
          <div className="text-center text-sm text-muted-foreground">
            <div className="font-medium">{product.name}</div>
            {product.barcode && <div className="font-mono text-xs">Code-barres: {product.barcode}</div>}
            
          </div>

          <div className="flex gap-3">
            <Button onClick={handlePrint} disabled={!qrCodeUrl} className="flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Imprimer
            </Button>
            <Button variant="outline" onClick={handleDownload} disabled={!qrCodeUrl} className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Télécharger
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};